// hooks/useCalendarNavigation.ts
import { useCallback, useMemo } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import { useEventsStore } from 'store/useEventsStore'
import { DayjsInput } from 'utils/dayjs'
import { MonthProps } from '~types'

const MONTH_FORMAT = 'MMM-YYYY'
const SELECTED_FORMAT = 'MMMM-YYYY'

export const useCalendarNavigation = (
  setSelectedDate: (date: DayjsInput) => void,
  setIsNavigationClicked: (value: boolean) => void
) => {
  const { selectedMonth, activeMonthList } = useEventsStore()

  // Normalize month list to Dayjs instances
  const parsedMonths = useMemo(() => activeMonthList.map(item => dayjs(item.month, MONTH_FORMAT)), [activeMonthList])

  const selectedMonthDate = useMemo(() => dayjs(selectedMonth.value, SELECTED_FORMAT), [selectedMonth.value])

  const setCurrentMonth = useCallback(
    (month: Dayjs) => {
      setSelectedDate(month.startOf('month').toDate())
    },
    [setSelectedDate]
  )

  const setSelectedMonth = useCallback(
    (month: MonthProps) => {
      const parsed = dayjs(month.value, SELECTED_FORMAT)
      setSelectedDate(parsed.startOf('month').toDate())
    },
    [setSelectedDate]
  )

  const handleNavigation = useCallback(
    (key: string) => {
      const currentIndex = parsedMonths.findIndex(m => m.isSame(selectedMonthDate, 'month'))

      if (currentIndex === -1) return
      setIsNavigationClicked(true)

      if (key === 'next' && currentIndex < parsedMonths.length - 1) {
        setCurrentMonth(parsedMonths[currentIndex + 1])
      } else if (key === 'previous' && currentIndex > 0) {
        setCurrentMonth(parsedMonths[currentIndex - 1])
      } else if (key === 'today') {
        const today = dayjs().startOf('month')
        const todayIndex = parsedMonths.findIndex(m => m.isSame(today, 'month'))
        if (todayIndex !== -1) setCurrentMonth(parsedMonths[todayIndex])
      }
    },
    [parsedMonths, selectedMonthDate, setIsNavigationClicked, setCurrentMonth]
  )

  return {
    handleNavigation,
    setCurrentMonth,
    setSelectedMonth
  }
}
